import React, { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Button } from "antd";

import { GET_STAND } from "graphql/query/stand-gql";
import { Icons } from "components/layout";
import { Loader } from "components/request-result";
import Localize, { localizeIt } from "components/service/localize";
import { useMe, useVarParam } from "components/use-hooks";

import CommentsHooks from "./hooks";
import CommentsHelpers from "./helpers";
import StandCommentItem from "./stand-comment-item";
import StandCommentModal from "./stand-comment-modal";

import "./stand-comments.scss";

const StandCommentsPage = ({ standId }) => {
    const { me } = useMe();
    const appParams = useVarParam();
    const { moment } = appParams.get();

    const { data: standData } = useQuery(GET_STAND, {
        variables: { id: standId },
        fetchPolicy: "cache-first"
    });

    const stand = standData?.stand || {};
    const standEeId = stand?.ee_id;

    const draftSession = CommentsHooks.useDraftSession();
    const api = CommentsHooks.useApi({ standId });

    appParams.set({
        headerTitle: localizeIt("PAGE.Title_StandComments"),
        bottomBar: true
    });

    useEffect(() => {
        appParams.set({ searchOn: false });
        return () => {
            appParams.set({ searchOn: true });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const comments = useMemo(() => {
        const items = api.commentsQuery?.data?.comments?.data || [];
        return items.map(CommentsHelpers.Mappers.mapComment);
    }, [api.commentsQuery?.data]);

    const {
        modalState,
        handleOpenAdd,
        handleOpenEdit,
        handleCloseModal,
        handleTextChange,
        handleAttachmentUpload,
        handleAttachmentDelete,
        handleSubmitModal,
        handleDeleteComment,
        isDraftCreating,
        loadingAttachmentCreate
    } = CommentsHooks.useCommentModal({ standId, standEeId, api, draftSession, comments });

    return (
        <div className="page stand-comments-page">
            <h3>{ stand?.exhibitor || "" }</h3>
            <div className="stand-comments-content">
                { api.commentsQuery.loading && <Loader type="block" /> }

                { !api.commentsQuery.loading && !comments.length &&
                    <div className="stand-comments-empty">
                        <p><Localize>STAND.Text_NoComments</Localize></p>
                        <Icons.CommentsIllustration />
                    </div>
                }

                { !api.commentsQuery.loading && !!comments.length &&
                    <ul className="stand-comments-list">
                        { comments.map((comment) => (
                            <StandCommentItem
                                key={ comment.id }
                                comment={ comment }
                                canManage={ Number(comment.authorId) === Number(me?.id) }
                                onEdit={ handleOpenEdit }
                                onDelete={ handleDeleteComment }
                                moment={ moment }
                            />
                        )) }
                    </ul>
                }
            </div>

            <div className="stand-comments-page-actions">
                <Button type="primary" onClick={ handleOpenAdd }>
                    <Icons.Cross className="icon-plus" />
                    <Localize>STAND.Button_Text_AddComment</Localize>
                </Button>
            </div>

            <StandCommentModal
                state={ modalState }
                onClose={ handleCloseModal }
                onSubmit={ handleSubmitModal }
                onTextChange={ handleTextChange }
                onAttachmentUpload={ handleAttachmentUpload }
                onAttachmentDelete={ handleAttachmentDelete }
                isDraftCreating={ isDraftCreating }
                updateLoading={ api.updateCommentResult.loading }
                loadingAttachmentCreate={ loadingAttachmentCreate }
            />
        </div>
    );
};

export default StandCommentsPage;
